import { StanModal } from '@/components';
import { SocialLinksSection } from '@/features/profile';
import { useProfileForm } from '@/features/profile/hooks/useProfileForm';
import { useUpdateProfile } from '@/features/profile/queries';
import { FormProvider } from 'react-hook-form';
import { stanNotify } from '@/lib/stanNotify';
import { isAxiosError } from 'axios';

interface SocialLinksModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export function SocialLinksModal({ isOpen, onClose, onSave }: SocialLinksModalProps) {
    const profileForm = useProfileForm();
    const { mutateAsync: updateProfileMutation } = useUpdateProfile();

    const handleClose = () => {
        // Reset form when closing without saving
        profileForm.resetForm();
        onClose();
    };

    const handleSave = async () => {
        // Validate form first
        const isValid = await profileForm.form.trigger();
        if (!isValid) {
            stanNotify({
                type: 'error',
                title: 'Validation Error',
                text: 'Please fix the validation errors before submitting.',
                duration: 5000,
            });
            return;
        }

        const formData = profileForm.form.getValues();

        try {
            await updateProfileMutation({ data: formData });

            // Reset form dirty state
            profileForm.form.reset(formData);

            stanNotify({
                type: 'success',
                title: 'Social Links Updated',
                text: 'Your social links have been updated successfully!',
                duration: 3000,
            });

            // Call the onSave callback to mark step as ready and refetch user
            onSave();
            handleClose();
        } catch (error) {
            let errorMessage = 'An error occurred while updating your social links. Please try again.';

            if (isAxiosError(error)) {
                errorMessage = error.response?.data?.message || error.message;
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }

            stanNotify({
                type: 'error',
                title: 'Update Failed',
                text: errorMessage,
                duration: 5000,
            });
        }
    };

    return (
        <StanModal
            id='onboarding-social-links-modal'
            isOpen={isOpen}
            title='Add Your Socials'
            onClose={handleClose}
            primaryActionLabel={profileForm.isSubmitting ? 'Saving...' : 'Save'}
            secondaryActionLabel='Cancel'
            primaryActionDisabled={profileForm.isSubmitting || !profileForm.isDirty}
            primaryActionLoading={profileForm.isSubmitting}
            onPrimaryAction={handleSave}
            onSecondaryAction={handleClose}
            size='md'
        >
            <FormProvider {...profileForm.form}>
                <div className='space-y-4'>
                    <p className='text-gray text-sm'>Connect all of your different social media accounts</p>
                    <SocialLinksSection disabled={profileForm.isSubmitting} />
                </div>
            </FormProvider>
        </StanModal>
    );
}
